import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { Subject, finalize, forkJoin, takeUntil } from 'rxjs';
import { IUser } from 'src/app/features/models/iuser.modal';
import { IDisposeSample } from 'src/app/shared/models/i-dispose-sample.modal';
import {
  IDisposeSamplePrintAssignee,
  IDisposeSamplePrintHistory,
} from 'src/app/shared/models/i-dispose-sample-print-history.modal';
import { UserService } from 'src/app/shared/services/user.service';
import { environment } from 'src/environments/environment';
import { AdminService } from '../admin.service';

@Component({
    selector: 'app-dispose-samples',
    templateUrl: './dispose-samples.component.html',
    styleUrls: ['./dispose-samples.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DisposeSamplesComponent implements OnInit, OnDestroy {
  @ViewChild('printHistoryDialog') printHistoryDialog!: TemplateRef<unknown>;
  private readonly destroyed$ = new Subject<boolean>();
  dataSource = new MatTableDataSource<IDisposeSample>();
  displayedColumns: string[] = ['received', 'companyName', 'projectName', 'jobNumber', 'state', 'storage'];
  filterForm: FormGroup;
  isLoading = false;
  hasGenerated = false;
  generatedFrom: Date | null = null;
  generatedTo: Date | null = null;
  users: IUser[] = [];
  assignedToId: string | null = null;
  printHistory = new MatTableDataSource<IDisposeSamplePrintHistory>();
  printHistoryColumns: string[] = ['printedAt', 'printedBy', 'dateRange', 'assignedTo'];
  isHistoryLoading = false;

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private adminService: AdminService,
    private userService: UserService,
    private dialog: MatDialog,
  ) {
    this.filterForm = this.fb.group({
      startDate: [null, Validators.required],
      endDate: [null, Validators.required],
    });
  }

  ngOnInit(): void {
    this.fetchUsers();
  }

  // Same users the worksheet "Assign To" dialog offers managers/admins: lab techs and lab managers
  fetchUsers(): void {
    forkJoin([
      this.userService.getUsersByGroupIdGraphApi(environment.labTechGroupId),
      this.userService.getUsersByGroupIdGraphApi(environment.labManagerGroupId),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: ([labTechs, labManagers]) => {
          // Role is needed by the API to create the app user the first time they are assigned
          this.users = [
            ...labTechs.value.map((user) => ({ ...user, role: 'Labtech' })),
            ...labManagers.value.map((user) => ({ ...user, role: 'Manager' })),
          ];
        },
        error: () => {
          this.users = [];
        },
      });
  }

  // Load fresh history each time the popup opens
  openPrintHistory(): void {
    this.fetchPrintHistory();
    this.dialog.open(this.printHistoryDialog, { width: '800px', maxWidth: '95vw' });
  }

  fetchPrintHistory(): void {
    this.isHistoryLoading = true;
    this.adminService
      .getDisposeSamplePrintHistory()
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => (this.isHistoryLoading = false)),
      )
      .subscribe({
        next: (history) => {
          this.printHistory.data = Array.isArray(history) ? history : [];
        },
        error: () => {
          this.printHistory.data = [];
        },
      });
  }

  get assignedToName(): string {
    const user = this.users.find((u) => u.id === this.assignedToId);
    return user ? `${user.givenName} ${user.surname}` : '';
  }

  reDirectUrl() {
    this.router.navigate(['dashboard']);
  }

  generate(): void {
    if (this.filterForm.invalid) {
      return;
    }
    const { startDate, endDate } = this.filterForm.value;
    this.isLoading = true;
    this.adminService
      .getDisposeSamples(this.toDateParam(startDate), this.toDateParam(endDate))
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => {
          this.isLoading = false;
          this.hasGenerated = true;
        }),
      )
      .subscribe({
        next: (samples) => {
          this.dataSource.data = Array.isArray(samples) ? samples : [];
          this.generatedFrom = startDate;
          this.generatedTo = endDate;
        },
        error: () => {
          this.dataSource.data = [];
        },
      });
  }

  // Hide the app header while printing, same as the worksheet print pages
  printResults(): void {
    this.recordPrint();
    document.body.classList.add('print-no-header');
    window.addEventListener('afterprint', () => document.body.classList.remove('print-no-header'), { once: true });
    window.print();
  }

  // Log the print (range of the generated results, assignee); the API stamps the user and time.
  // A failed log does not stop the print.
  private recordPrint(): void {
    if (!this.generatedFrom || !this.generatedTo) {
      return;
    }
    this.adminService
      .addDisposeSamplePrintHistory({
        fromDate: this.toDateParam(this.generatedFrom),
        toDate: this.toDateParam(this.generatedTo),
        assignedTo: this.toAssignee(this.users.find((u) => u.id === this.assignedToId)),
      })
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (entry) => {
          this.printHistory.data = [entry, ...this.printHistory.data];
        },
      });
  }

  private toAssignee(user: IUser | undefined): IDisposeSamplePrintAssignee | null {
    if (!user) {
      return null;
    }
    return {
      aadObjectId: user.id,
      firstName: user.givenName,
      lastName: user.surname,
      userName: user.displayName,
      email: user.userPrincipalName,
      role: user.role,
    };
  }

  // Flagged states come back from the API prefixed with ">>>>"
  isFlaggedState(state: string | null): boolean {
    return !!state?.startsWith('>>>>');
  }

  // Send the picked calendar day as yyyy-MM-dd so it is not shifted by the browser's time zone
  private toDateParam(date: Date): string {
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  ngOnDestroy(): void {
    document.body.classList.remove('print-no-header');
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
